import type { ReportedPension } from '@nxsys/types/models/payroll';

/**
 * Pension figures shown on screens and exports.
 *
 * The backend owns the salary sacrifice rule and sends the result as
 * `reportedPension`: under salary sacrifice the employee figures are 0 and the
 * employer figure includes the sacrificed amount. The raw `payeeUmbrellaValue`
 * keeps the sacrificed amount in `employeePension` because gross, tax, NI and
 * NMW are built on it, so screens must not show the raw split for those
 * payrolls. Nothing here re-derives the rule; it only picks which value to show.
 */

type RawPensionValues = {
  employeePension?: number | null;
  employersPension?: number | null;
  avcEEPension?: number | null;
  avcERPension?: number | null;
  refundEEPension?: number | null;
  refundERPension?: number | null;
};

/** Any payroll-shaped object: payroll, draft payroll, payslip payrollRef. */
export type PayrollPensionSource =
  | {
      reportedPension?: ReportedPension | null;
      payeeUmbrellaValue?: RawPensionValues | null;
    }
  | null
  | undefined;

export type PensionField =
  | 'employeePension'
  | 'employersPension'
  | 'avcEEPension'
  | 'avcERPension';

/**
 * The backend's reported split, or the raw split when the response predates
 * it (old cached data). The raw fallback never reports a sacrifice.
 */
export const getReportedPension = (
  payroll: PayrollPensionSource,
): ReportedPension => {
  if (payroll?.reportedPension) return payroll.reportedPension;

  const raw = payroll?.payeeUmbrellaValue;
  return {
    isSalarySacrifice: false,
    salarySacrificeAmount: 0,
    employeePension: Number(raw?.employeePension) || 0,
    employersPension: Number(raw?.employersPension) || 0,
    avcEEPension: Number(raw?.avcEEPension) || 0,
    avcERPension: Number(raw?.avcERPension) || 0,
  };
};

/**
 * Value to display for one pension field. Under salary sacrifice it is the
 * reported value. Otherwise the raw field is returned untouched, so payrolls
 * without a sacrifice render exactly as they did before the reported split
 * existed (a missing value still renders as the formatter's blank).
 */
export const getShownPensionValue = (
  payroll: PayrollPensionSource,
  field: PensionField,
): number => {
  const reported = getReportedPension(payroll);
  if (reported.isSalarySacrifice) return reported[field];
  // The payroll models declare these fields as number; legacy rows (and the
  // empty Ireland payeeUmbrellaValue) can omit them, which callers' money
  // formatters already handle, so the raw value is passed through as is.
  return payroll?.payeeUmbrellaValue?.[field] as number;
};

const negatedRefund = (refund: number | null | undefined): number =>
  refund ? -Math.abs(refund) : 0;

/**
 * Employee pension for pension columns. Under salary sacrifice this is the
 * reported 0. Otherwise it is the raw contribution, falling back to the
 * refunded amount (shown negative) in a period where a refund replaced the
 * deduction.
 */
export const getShownEmployeePension = (
  payroll: PayrollPensionSource,
): number => {
  const reported = getReportedPension(payroll);
  if (reported.isSalarySacrifice) return reported.employeePension;
  const raw = payroll?.payeeUmbrellaValue;
  return raw?.employeePension || negatedRefund(raw?.refundEEPension);
};

/**
 * Employer pension for pension columns. Under salary sacrifice this is the
 * reported employer figure, which includes the sacrificed amount. Otherwise
 * it is the raw contribution with the same refund fallback as the employee
 * column.
 */
export const getShownEmployersPension = (
  payroll: PayrollPensionSource,
): number => {
  const reported = getReportedPension(payroll);
  if (reported.isSalarySacrifice) return reported.employersPension;
  const raw = payroll?.payeeUmbrellaValue;
  return raw?.employersPension || negatedRefund(raw?.refundERPension);
};
